import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ShieldCheck, Check, X, AlertTriangle, FileSignature, Download, Loader2 } from "lucide-react";
import { carregarPortal, visualizarPortal, assinarPortal, recusarPortal } from "../services/assinaturas";
import { anexoUrlAbsoluto } from "../services/tarefas";
import { useBranding } from "../hooks/useBranding";

export function Assinar() {
  const { token } = useParams<{ token: string }>();
  const branding = useBranding();
  const [aceitouConsentimento, setAceitou] = useState(false);
  const [documento, setDocumento] = useState("");
  const [showRecusar, setShowRecusar] = useState(false);
  const [motivoRecusa, setMotivoRecusa] = useState("");
  const [erroLocal, setErroLocal] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["assinar", token],
    queryFn: () => carregarPortal(token!),
    enabled: !!token,
    retry: false,
  });

  // Marca visualização ao abrir
  useEffect(() => {
    if (data?.signatario.status === "pendente") {
      visualizarPortal(token!).catch(() => null);
    }
  }, [data?.signatario.id, token]);

  const assinarM = useMutation({
    mutationFn: () => assinarPortal(token!, { documento, aceitouConsentimento }),
    onSuccess: () => refetch(),
    onError: (e: any) => setErroLocal(e?.response?.data?.error?.message || "Erro ao assinar"),
  });

  const recusarM = useMutation({
    mutationFn: () => recusarPortal(token!, motivoRecusa),
    onSuccess: () => refetch(),
    onError: (e: any) => setErroLocal(e?.response?.data?.error?.message || "Erro ao recusar"),
  });

  if (isLoading) {
    return <Centro><Loader2 className="animate-spin text-blue-600" size={32} /></Centro>;
  }
  if (error || !data) {
    return <Centro><Mensagem icon={<AlertTriangle className="text-red-500" size={32} />} titulo="Link inválido ou expirado" texto="Confira o link recebido ou peça um novo." /></Centro>;
  }

  const { documento: doc, signatario } = data;
  const url = anexoUrlAbsoluto(doc.arquivoUrl);
  const jaAssinou = signatario.status === "assinado";
  const recusou = signatario.status === "recusado";
  const finalizado = doc.status === "assinado";
  const cancelado = doc.status === "cancelado" || doc.status === "expirado";

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950">
      <header className="bg-slate-900 text-white px-4 sm:px-6 py-3 flex items-center gap-3 shadow">
        <FileSignature size={20} className="text-blue-300" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold truncate">{branding?.nome || "RECDC OS"}</p>
          <p className="text-[11px] text-slate-300">Assinatura eletrônica de documento</p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 sm:p-6 space-y-4">
        <section className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
          <h1 className="text-lg font-bold text-slate-800 dark:text-slate-100">{doc.titulo}</h1>
          {doc.descricao && <p className="text-sm text-slate-600 dark:text-slate-300 mt-1 whitespace-pre-wrap">{doc.descricao}</p>}
          <div className="flex flex-wrap gap-3 mt-3 text-[11px] text-slate-500">
            <span className="font-mono">Código: {doc.codigoVerificacao}</span>
            <span className="font-mono">Hash: {doc.arquivoHash.slice(0, 20)}…</span>
          </div>
          <div className="mt-3">
            <p className="text-sm text-slate-700 dark:text-slate-200">
              Olá <strong>{signatario.nome}</strong>{signatario.papel && <> ({signatario.papel})</>}!{" "}
              Você foi convidado(a) a assinar este documento.
            </p>
          </div>
        </section>

        {/* PDF */}
        <section className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100 dark:border-slate-700">
            <span className="text-xs font-medium text-slate-700 dark:text-slate-200">Documento</span>
            <a href={url} target="_blank" rel="noreferrer" className="text-xs flex items-center gap-1 text-blue-600 hover:text-blue-700">
              <Download size={12} /> Baixar PDF
            </a>
          </div>
          <iframe src={url} className="w-full" style={{ height: "70vh" }} title="Documento" />
        </section>

        {/* Status / ações */}
        {jaAssinou && (
          <Mensagem cor="emerald" icon={<Check className="text-emerald-600" size={22} />} titulo="Você já assinou este documento" texto={finalizado ? "Todos os signatários assinaram. Você pode baixar o PDF final pelo link acima." : "Aguardando os outros signatários."} />
        )}
        {recusou && (
          <Mensagem cor="red" icon={<X className="text-red-500" size={22} />} titulo="Você recusou este documento" texto={signatario.recusadoEm ? `Em ${new Date(signatario.recusadoEm).toLocaleString("pt-BR")}` : ""} />
        )}
        {cancelado && (
          <Mensagem cor="slate" icon={<AlertTriangle className="text-slate-500" size={22} />} titulo={doc.status === "cancelado" ? "Documento cancelado" : "Documento expirado"} texto="Não é mais possível assinar." />
        )}

        {!jaAssinou && !recusou && !cancelado && (
          <section className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 space-y-3">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-100 flex items-center gap-2">
              <ShieldCheck size={16} className="text-blue-600" /> Assinatura
            </h2>

            <div>
              <label className="text-xs text-slate-600 dark:text-slate-300">Confirme seu CPF/CNPJ</label>
              <input
                type="text"
                value={documento}
                onChange={(e) => setDocumento(e.target.value)}
                placeholder={signatario.documento || "000.000.000-00"}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 outline-none text-sm focus:border-blue-500"
              />
              <p className="text-[10px] text-slate-400 mt-0.5">Usado como evidência da identidade do assinante. Será incluído no certificado.</p>
            </div>

            {doc.textoConsentimento && (
              <div className="text-xs text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded p-3 max-h-40 overflow-y-auto whitespace-pre-wrap">
                {doc.textoConsentimento}
              </div>
            )}

            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={aceitouConsentimento} onChange={(e) => setAceitou(e.target.checked)} className="mt-0.5" />
              <span className="text-xs text-slate-700 dark:text-slate-200">Li e aceito o documento e o termo acima.</span>
            </label>

            {erroLocal && (
              <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{erroLocal}</div>
            )}

            <div className="flex justify-between gap-2 flex-wrap">
              <button
                onClick={() => setShowRecusar(true)}
                className="text-sm flex items-center gap-1 bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-2 rounded"
              >
                <X size={14} /> Recusar
              </button>
              <button
                onClick={() => assinarM.mutate()}
                disabled={!aceitouConsentimento || assinarM.isPending}
                className="text-sm flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded disabled:opacity-50"
              >
                <Check size={14} /> {assinarM.isPending ? "Assinando…" : "Assinar agora"}
              </button>
            </div>
          </section>
        )}

        <p className="text-[10px] text-slate-400 text-center pt-4">
          Esta assinatura segue a Lei 14.063/2020 e MP 2.200-2/2001 — assinatura eletrônica avançada com link autenticado, hash do documento e trilha de auditoria.
        </p>
      </main>

      {showRecusar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowRecusar(false)}>
          <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 space-y-3">
              <h3 className="font-semibold text-slate-800 dark:text-slate-100">Recusar documento</h3>
              <textarea
                value={motivoRecusa}
                onChange={(e) => setMotivoRecusa(e.target.value)}
                rows={3}
                placeholder="Motivo (opcional)"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 outline-none text-sm"
              />
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowRecusar(false)} className="px-3 py-1.5 rounded text-sm bg-slate-100 hover:bg-slate-200">Voltar</button>
                <button
                  onClick={() => { recusarM.mutate(); setShowRecusar(false); }}
                  className="px-4 py-1.5 rounded text-sm bg-red-600 hover:bg-red-700 text-white"
                >
                  Confirmar recusa
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Centro({ children }: { children: React.ReactNode }) {
  return <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-slate-950 p-6">{children}</div>;
}

function Mensagem({ icon, titulo, texto, cor = "slate" }: { icon: React.ReactNode; titulo: string; texto: string; cor?: string }) {
  const corBg = cor === "emerald" ? "bg-emerald-50 border-emerald-200" : cor === "red" ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200";
  return (
    <div className={`max-w-md mx-auto rounded-xl border p-5 text-center ${corBg}`}>
      <div className="flex justify-center mb-2">{icon}</div>
      <p className="font-semibold text-slate-800">{titulo}</p>
      {texto && <p className="text-sm text-slate-600 mt-1">{texto}</p>}
    </div>
  );
}
